import axios, { isAxiosError } from "axios"
import { OpaqueSparqlResults } from "./sparqlResultHelpers"
import { DeferredPromise } from "./DeferredPromise"

type ResultHolder = {
  result: OpaqueSparqlResults
  isLatest: () => boolean
}

type QueryString = string

type TemplateId = string

export class ErrorNeedAuth extends Error {}

const state = {
  instances: new Map<TemplateId, SparqlTemplate>(),
  cache: new Map<QueryString, OpaqueSparqlResults>()
}

export class SparqlTemplate {
  lastPromise: PromiseLike<ResultHolder> | null = null
  sparqlEndpoint: string
  cancelActiveRequest: null | (() => void) = null

  constructor(sparqlEndpoint: string, templateId: TemplateId) {
    this.sparqlEndpoint = sparqlEndpoint
    state.instances.set(templateId, this)
  }

  async fetch(query: QueryString): Promise<ResultHolder> {
    const promise = new DeferredPromise<ResultHolder>()
    this.lastPromise = promise

    const isLatest = () => promise === this?.lastPromise
    this.cancelActiveRequest?.()

    if (state.cache.has(query)) {
      const cachedResults = state.cache.get(query)
      if (!cachedResults) throw new Error('Cache problem!')
      setTimeout(() => promise.resolve({ isLatest, result: cachedResults }), 10)
      return promise
    }

    try {
      const data = new URLSearchParams()
      data.set('infer', String(false))
      data.set('sameAs', String(false))
      data.set('query', query)

      const response = await axios({
        method: 'post',
        url: this.sparqlEndpoint,
        maxContentLength: Infinity,
        maxBodyLength: Infinity,
        data,
        // @ts-ignore
        withCredentials: document.withCredentials ?? true,
        headers:{
          Accept: 'application/sparql-results+json,*/*;q=0.9',
          'Content-Type': 'application/x-www-form-urlencoded'
        },
        cancelToken: new axios.CancelToken((c) => this.cancelActiveRequest = c),
      })

      this.cancelActiveRequest = null
      const result = response.data

      if (shouldCacheQuery(query)) {
        state.cache.set(query, result)
      }

      promise.resolve({
        isLatest,
        result
      })
      return promise
    } catch (error) {
      if (isAxiosError(error) && (error.response?.status === 401 || error.response?.status === 403))
        throw new ErrorNeedAuth()
      throw error
    }
  }
}

function shouldCacheQuery(query: QueryString) {
  return !query.includes("#nocache")
}
